import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  FaDownload,
  FaTimes,
  FaFilter,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa";
import { GrPowerReset } from "react-icons/gr";
import {
  fetchProjectsByOrg,
  fetchProjectsByTeamMember,
  fetchProjectDetails,
  updateProject,
} from "../Api/ViewProjects";
import {
  fetchProjectBaseByOrg,
  fetchProjectBaseByOrgAndMember,
} from "../Api/ViewProjects";
import { getTeamsByOrg } from "../Api/TeamMaster";
import { getAllProjectStatus } from "../Api/ProjectStatusApi";
import { fetchCustomerMasterByOrgId } from "../Api/ViewCustomerMaster";
import { fetchProjectTypes, fetchResourceByOrgId } from "../Api/InvoiceApi";
import { getProjectMetaNames } from "../Api/ProjectMaster";
import "./ViewProject.css";

// ============================================================
// Utility Functions
// ============================================================
const formatWithId = (name, id) => {
  if (!name && !id) return "";
  if (name && id) return `${name}`;
  return name || id;
};

// ============================================================
// Data Mapping Function - Convert API snake_case to camelCase
// ============================================================
const mapApiProjectToComponent = (apiProject) => {
  return {
    projectId: apiProject.project_id,
    prTitle: apiProject.pr_Title,
    prDescription: apiProject.pr_Description,
    prTags: apiProject.pr_Tags,
    prStartDate: apiProject.pr_Start_Date,
    prEndDate: apiProject.pr_End_Date,
    prClient: apiProject.pr_Client,
    prTypes: apiProject.pr_Types,
    projectStatus: apiProject.project_Status,
    projectSubStatus: apiProject.projectSubStatus,
    projectCategory: apiProject.project_Category,
    comments: apiProject.comments,
    salespersonCode: apiProject.salesperson_Code,
    currentProjectManager: apiProject.current_Project_Manager,
    projectPriority: apiProject.projectPriority,
    teamAssigned: apiProject.team_Assigned,
    users_Id: apiProject.users_Id,
    isFavourite: apiProject.isFavourite,
    active: apiProject.active,
    org_ID: apiProject.org_ID,
    prozma_Pr_Id: apiProject.prozma_Pr_Id,
    clientName: apiProject.clientName,
    clientShortNames: apiProject.clientShortNames,
    projectTypeDesc: apiProject.projectTypeDesc,
    teamName: apiProject.teamName,
    project_status_desc: apiProject.project_status_desc,
    projectSubStatusDesc: apiProject.projectSubStatusDesc,
    createdOn_date: apiProject.createdOn_date,
    createdOn_time: apiProject.createdOn_time,
    changedOn_date: apiProject.changedOn_date,
    changedOn_time: apiProject.changedOn_time,
    changedBy: apiProject.changedBy,
    salesPersonName: apiProject.salesPersonName,
    currentProjectManagerName: apiProject.currentProjectManagerName,
    organisation_Name: apiProject.organisation_Name,
    org_Short_name: apiProject.org_Short_name,
    revenue_allowed: apiProject.revenue_allowed,
    cost_allowed: apiProject.cost_allowed,
    companycode: apiProject.companycode,
    companyName: apiProject.companyName,
    companyShortName: apiProject.companyShortName,
    shortName: apiProject.shortName,
  };
};

// Priority options
const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low", color: "#10b981" },
  { value: "Medium", label: "Medium", color: "#f59e0b" },
  { value: "High", label: "High", color: "#ef4444" },
  { value: "Very High", label: "Very High", color: "#dc2626" },
];

const getPriorityColor = (priority) => {
  switch (priority) {
    case "Very High":
      return "#dc2626";
    case "High":
      return "#ef4444";
    case "Medium":
      return "#f59e0b";
    case "Low":
      return "#10b981";
    default:
      return "#6b7280";
  }
};

// ============================================================
// Checkbox Multi-Select Component - WORKING VERSION
// ============================================================
// ============================================================
// Checkbox Multi-Select Component with Search
// ============================================================
const CheckboxMultiSelect = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Calculate and update dropdown position
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - dropdownHeight - 5;
      } else {
        top = rect.bottom + 5;
      }

      setDropdownStyle({
        position: "fixed",
        top: `${top + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        zIndex: 999999,
      });
    }
  }, []);

  // Handle opening the dropdown
  const handleOpen = () => {
    updatePosition();
    setIsOpen(true);
  };

  // Update position on scroll and resize when open
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  const handleCheckboxChange = (value) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (
      selectedValues.length === filteredOptions.length &&
      filteredOptions.length > 0
    ) {
      // Remove all filtered options from selection
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelectedValues = selectedValues.filter(
        (v) => !filteredValues.includes(v),
      );
      onChange(newSelectedValues);
    } else {
      // Add all filtered options to selection
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelectedValues = [
        ...new Set([...selectedValues, ...filteredValues]),
      ];
      onChange(newSelectedValues);
    }
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm]);

  const selectedLabels = selectedValues
    .map((val) => options.find((opt) => opt.value === val)?.label)
    .filter(Boolean);

  const isAllFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedValues.includes(opt.value));

  return (
    <div className="vp2-checkbox-multiselect" style={{ position: "relative" }}>
      <label>{label}</label>
      <div
        ref={triggerRef}
        className="vp2-multiselect-trigger"
        onClick={handleOpen}
      >
        {selectedLabels.length > 0 ? (
          <span className="vp2-selected-items">
            {selectedLabels.slice(0, 2).join(", ")}
            {selectedLabels.length > 2 && ` +${selectedLabels.length - 2}`}
          </span>
        ) : (
          <span className="vp2-placeholder">{placeholder}</span>
        )}
        <span className="vp2-dropdown-arrow">▼</span>
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="vp2-multiselect-dropdown"
          style={dropdownStyle}
        >
          {/* Search Input */}
          <div className="vp2-search-input-container">
            <input
              ref={searchInputRef}
              type="text"
              className="vp2-dropdown-search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className="vp2-clear-search"
                onClick={() => setSearchTerm("")}
              >
                ×
              </button>
            )}
          </div>

          <div className="vp2-select-all-row">
            <label className="vp2-checkbox-label">
              <input
                type="checkbox"
                checked={isAllFilteredSelected && filteredOptions.length > 0}
                onChange={handleSelectAll}
              />
              <span>
                Select All{" "}
                {filteredOptions.length !== options.length &&
                  `(${filteredOptions.length})`}
              </span>
            </label>
          </div>

          <div className="vp2-options-list">
            {filteredOptions.length === 0 ? (
              <div className="vp2-no-options">No matching options</div>
            ) : (
              filteredOptions.map((option) => (
                <label key={option.value} className="vp2-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))
            )}
          </div>

          <div className="vp2-dropdown-actions">
            <button
              type="button"
              className="vp2-apply-btn"
              onClick={() => {
                setIsOpen(false);
                setSearchTerm("");
              }}
            >
              Apply ({selectedValues.length} selected)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Main Component
// ============================================================
const ViewProjects = () => {
  const navigate = useNavigate();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = String(userDetails?.userId);
  const orgId = String(userDetails?.organisationId);
  const roles = userDetails?.roles || [];
  const isAdmin = roles.includes("Admin");

  // ========== State ==========
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Active/Inactive Toggle State - Default to Active (true)
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Meta Data
  const [metaDataMap, setMetaDataMap] = useState({
    type: {},
    status: {},
    substatus: {},
  });
  const [isMetaDataLoaded, setIsMetaDataLoaded] = useState(false);

  // Master Data
  const [customers, setCustomers] = useState([]);
  const [customerMap, setCustomerMap] = useState({});
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectTypeMap, setProjectTypeMap] = useState({});
  const [teams, setTeams] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  const [resources, setResources] = useState([]);
  const [activeResources, setActiveResources] = useState([]);
  const [resourceMap, setResourceMap] = useState({});
  const [projectStatusList, setProjectStatusList] = useState([]);

  // Filter States - Using arrays for checkbox multi-select
  const [globalSearch, setGlobalSearch] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [selectedSubStatusFilter, setSelectedSubStatusFilter] = useState([]);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState([]);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
  const [selectedSalespersonFilter, setSelectedSalespersonFilter] = useState(
    [],
  );
  const [selectedProjectManagerFilter, setSelectedProjectManagerFilter] =
    useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState([]);
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  // UI State
  const [showFilters, setShowFilters] = useState(true);

  // Column Order
  const defaultColumnOrder = [
    "index",
    "projectId",
    "title",
    "description",
    "startDate",
    "endDate",
    "client",
    "type",
    "status",
    "subStatus",
    "category",
    "priority",
    "salesperson",
    "projectManager",
  ];
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem("vp2_columnOrder");
    const savedOrder = saved ? JSON.parse(saved) : [];
    return [
      ...defaultColumnOrder.filter((col) => !savedOrder.includes(col)),
      ...savedOrder,
    ];
  });

  const [projectData, setProjectData] = useState({
    prTitle: "",
    prDescription: "",
    prTags: "",
    prStartDate: "",
    prEndDate: "",
    prClient: "",
    prTypes: "",
    teamAssigned: "",
    projectStatus: "",
    projectCategory: "",
    comments: "",
    salespersonCode: "",
    currentProjectManager: "",
    projectPriority: "",
  });

  // ========== Helper Functions ==========
  const getDisplayNameFromGuid = useCallback(
    (guid, entityType) => {
      if (!guid) return "—";
      const key = String(guid).toLowerCase();
      return metaDataMap[entityType]?.[key] || "—";
    },
    [metaDataMap],
  );

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return <span className="vp2-sort-icon">↕</span>;
    return sortConfig.direction === "asc" ? (
      <span className="vp2-sort-icon">↑</span>
    ) : (
      <span className="vp2-sort-icon">↓</span>
    );
  };

  // ========== Data Fetching ==========
  const fetchMetaNames = async (projectsList) => {
    if (!projectsList?.length) return;
    try {
      const typeGuids = [
        ...new Set(projectsList.map((p) => p.prTypes).filter(Boolean)),
      ].join(",");
      const statusGuids = [
        ...new Set(projectsList.map((p) => p.projectStatus).filter(Boolean)),
      ].join(",");
      const subStatusGuids = [
        ...new Set(projectsList.map((p) => p.projectSubStatus).filter(Boolean)),
      ].join(",");
      const response = await getProjectMetaNames(
        typeGuids,
        statusGuids,
        subStatusGuids,
      );
      const typeMap = {},
        statusMap = {},
        substatusMap = {};
      response.forEach((item) => {
        const key = item.guid.toLowerCase();
        if (item.entity === "TYPE") typeMap[key] = item.name;
        if (item.entity === "STATUS") statusMap[key] = item.name;
        if (item.entity === "SUBSTATUS") substatusMap[key] = item.name;
      });
      setMetaDataMap({
        type: typeMap,
        status: statusMap,
        substatus: substatusMap,
      });
    } catch (err) {
      console.error("Meta fetch error:", err);
    } finally {
      setIsMetaDataLoaded(true);
    }
  };

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const rawData = isAdmin
          ? await fetchProjectBaseByOrg(orgId)
          : await fetchProjectBaseByOrgAndMember(orgId, userId);
        console.log("Raw API data:", rawData);

        const mappedData = (rawData || []).map(mapApiProjectToComponent);
        console.log("Mapped projects:", mappedData);

        setProjects(mappedData);
        await fetchMetaNames(mappedData);
      } catch (error) {
        toast.error("Failed to fetch projects");
        console.error("Error fetching projects:", error);
      } finally {
        setProjectsLoading(false);
      }
    };
    if (orgId && userId) loadProjects();
  }, [orgId, userId, isAdmin]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const data = await fetchResourceByOrgId(orgId);
        console.log("Fetched resources:", data);

        const onlyActiveResources = data.filter(
          (resource) => resource.active === true,
        );
        console.log("Active resources only:", onlyActiveResources);

        const uniqueActiveResources = Array.from(
          new Map(
            onlyActiveResources.map((item) => [item.resourceId, item]),
          ).values(),
        );

        const uniqueAllResources = Array.from(
          new Map(data.map((item) => [item.resourceId, item])).values(),
        );
        setResources(uniqueAllResources || []);
        setActiveResources(uniqueActiveResources || []);
        setResourceMap(
          Object.fromEntries(
            uniqueAllResources.map((r) => [r.resourceId, r.name]),
          ),
        );
      } catch (error) {
        toast.error("Failed to load resources");
      }
    };
    if (orgId) loadResources();
  }, [orgId]);

  useEffect(() => {
    const loadProjectStatus = async () => {
      try {
        const data = await getAllProjectStatus();
        const activeStatus = data.filter((s) => s.active);
        setProjectStatusList(activeStatus);
      } catch (error) {
        toast.error("Failed to load statuses");
      }
    };
    loadProjectStatus();
  }, []);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomerMasterByOrgId(orgId);
        console.log("Fetched customers:", data);
        setCustomers(data || []);
        setCustomerMap(
          Object.fromEntries(
            data.map((c) => [
              c.customerCode,
              {
                shortName: c.shortNames,
                fullName: c.name || c.shortNames,
              },
            ]),
          ),
        );
      } catch (error) {
        toast.error("Failed to load customers");
      }
    };
    if (orgId) loadCustomers();
  }, [orgId]);

  useEffect(() => {
    const loadProjectTypes = async () => {
      try {
        const data = await fetchProjectTypes(orgId);
        const activeTypes = data.filter((t) => t.active);
        setProjectTypes(activeTypes);
        setProjectTypeMap(
          Object.fromEntries(
            activeTypes.map((t) => [t.projectTypeId, t.projectTypeDesc]),
          ),
        );
      } catch (error) {
        toast.error("Failed to load project types");
      }
    };
    if (orgId) loadProjectTypes();
  }, [orgId]);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await getTeamsByOrg(orgId);
        setTeams(data || []);
        setTeamMap(Object.fromEntries(data.map((t) => [t.teamId, t.teamName])));
      } catch (error) {
        toast.error("Failed to load teams");
      }
    };
    if (orgId) loadTeams();
  }, [orgId]);

  // ========== Derived Data for Checkbox Options ==========
  const statusOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.status).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.status]);

  const subStatusOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.substatus).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.substatus]);

  const typeOptions = useMemo(() => {
    const unique = new Map();
    Object.entries(metaDataMap.type).forEach(([guid, name]) => {
      if (!unique.has(name) && name !== guid) unique.set(name, guid);
    });
    return Array.from(unique.entries())
      .map(([name, guid]) => ({ value: guid, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [metaDataMap.type]);

  const customerOptions = useMemo(() => {
    const uniqueCustomers = Array.from(
      new Map(customers.map((c) => [c.customerCode, c])).values(),
    );
    return uniqueCustomers
      .map((c) => ({ value: c.customerCode, label: c.shortNames }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const resourceOptions = useMemo(() => {
    const uniqueMap = new Map();
    activeResources.forEach((resource) => {
      if (!uniqueMap.has(resource.resourceId)) {
        uniqueMap.set(resource.resourceId, {
          value: resource.resourceId,
          label: resource.name,
        });
      }
    });
    return Array.from(uniqueMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [activeResources]);

  // Filtered Projects
  const filteredProjects = useMemo(() => {
    return projects
      .filter((project) => {
        if (showActiveOnly && project.active !== 1) return false;

        const searchMatch = globalSearch
          ? String(project.projectId)
              .toLowerCase()
              .includes(globalSearch.toLowerCase()) ||
            project.prTitle?.toLowerCase().includes(globalSearch.toLowerCase())
          : true;

        if (!searchMatch) return false;
        if (
          selectedStatusFilter.length &&
          !selectedStatusFilter.includes(project.projectStatus)
        )
          return false;
        if (
          selectedSubStatusFilter.length &&
          !selectedSubStatusFilter.includes(project.projectSubStatus)
        )
          return false;
        if (
          selectedCustomerFilter.length &&
          !selectedCustomerFilter.includes(project.prClient)
        )
          return false;
        if (
          selectedTypeFilter.length &&
          !selectedTypeFilter.includes(project.prTypes)
        )
          return false;
        if (
          selectedSalespersonFilter.length &&
          !selectedSalespersonFilter.includes(project.salespersonCode)
        )
          return false;
        if (
          selectedProjectManagerFilter.length &&
          !selectedProjectManagerFilter.includes(project.currentProjectManager)
        )
          return false;
        if (
          selectedCategoryFilter &&
          project.projectCategory !== selectedCategoryFilter
        )
          return false;
        if (
          selectedPriorityFilter.length &&
          !selectedPriorityFilter.includes(project.projectPriority)
        )
          return false;
        if (
          startDateFilter &&
          new Date(project.prStartDate) < new Date(startDateFilter)
        )
          return false;
        if (
          endDateFilter &&
          new Date(project.prEndDate) > new Date(endDateFilter)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;

        if (sortConfig.key === "projectPriority") {
          const priorityOrder = { Low: 1, Medium: 2, High: 3, "Very High": 4 };
          const valA = priorityOrder[a.projectPriority] || 0;
          const valB = priorityOrder[b.projectPriority] || 0;
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        }

        const valA = a[sortConfig.key]
          ? new Date(a[sortConfig.key]).getTime()
          : 0;
        const valB = b[sortConfig.key]
          ? new Date(b[sortConfig.key]).getTime()
          : 0;
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      });
  }, [
    projects,
    globalSearch,
    selectedStatusFilter,
    selectedSubStatusFilter,
    selectedCustomerFilter,
    selectedTypeFilter,
    selectedSalespersonFilter,
    selectedProjectManagerFilter,
    selectedCategoryFilter,
    selectedPriorityFilter,
    startDateFilter,
    endDateFilter,
    sortConfig,
    showActiveOnly,
  ]);

  // ========== Event Handlers ==========
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const XLSX = await import("xlsx");
      const worksheetData = [
        [
          "S.No",
          "Project ID",
          "Title",
          "Description",
          "Start Date",
          "End Date",
          "Client",
          "Type",
          "Status",
          "Sub Status",
          "Category",
          "Priority",
          "Salesperson",
          "Project Manager",
          "Status",
        ],
        ...filteredProjects.map((p, i) => [
          i + 1,
          p.projectId,
          p.prTitle,
          p.prDescription,
          p.prStartDate ? moment(p.prStartDate).format("YYYY-MM-DD") : "",
          p.prEndDate ? moment(p.prEndDate).format("YYYY-MM-DD") : "",
          customerMap[p.prClient]?.fullName ||
            customerMap[p.prClient]?.shortName ||
            formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
            "—",
          getDisplayNameFromGuid(p.prTypes, "type"),
          getDisplayNameFromGuid(p.projectStatus, "status"),
          getDisplayNameFromGuid(p.projectSubStatus, "substatus"),
          p.projectCategory || "",
          p.projectPriority || "—",
          formatWithId(resourceMap[p.salespersonCode], p.salespersonCode),
          formatWithId(
            resourceMap[p.currentProjectManager],
            p.currentProjectManager,
          ),
          p.active === 1 ? "Active" : "Inactive",
        ]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");
      XLSX.writeFile(
        wb,
        `Projects_${orgId}_${moment().format("YYYYMMDD")}.xlsx`,
      );
      toast.success("Downloaded successfully!");
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleProjectSelect = async (projectId) => {
    setSelectedProjectId(projectId);
    try {
      setIsLoading(true);
      const data = await fetchProjectDetails(projectId);
      setProjectData({
        prTitle: data.pr_Title || data.prTitle || "",
        prDescription: data.pr_Description || data.prDescription || "",
        prTags: data.pr_Tags || data.prTags || "",
        prStartDate:
          data.pr_Start_Date || data.prStartDate
            ? moment(data.pr_Start_Date || data.prStartDate).format(
                "YYYY-MM-DD",
              )
            : "",
        prEndDate:
          data.pr_End_Date || data.prEndDate
            ? moment(data.pr_End_Date || data.prEndDate).format("YYYY-MM-DD")
            : "",
        prClient: data.pr_Client || data.prClient || "",
        prTypes: data.pr_Types || data.prTypes || "",
        teamAssigned: data.team_Assigned || data.teamAssigned || "",
        projectStatus: data.project_Status || data.projectStatus || "",
        projectCategory: data.project_Category || data.projectCategory || "",
        comments: data.comments || "",
        salespersonCode: data.salesperson_Code || data.salespersonCode || "",
        currentProjectManager:
          data.current_Project_Manager || data.currentProjectManager || "",
        projectPriority: data.projectPriority || "",
      });
    } catch (error) {
      toast.error("Failed to fetch project details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProjectId) return toast.error("Select a project");
    try {
      setIsLoading(true);
      await updateProject(selectedProjectId, projectData);
      toast.success("Project updated!");
      const rawData = isAdmin
        ? await fetchProjectBaseByOrg(orgId)
        : await fetchProjectBaseByOrgAndMember(orgId, userId);
      const mappedData = (rawData || []).map(mapApiProjectToComponent);
      setProjects(mappedData);
      setShowEditModal(false);
    } catch (error) {
      toast.error("Update failed");
      console.error("Update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAllFilters = () => {
    setGlobalSearch("");
    setSelectedStatusFilter([]);
    setSelectedSubStatusFilter([]);
    setSelectedCustomerFilter([]);
    setSelectedTypeFilter([]);
    setSelectedSalespersonFilter([]);
    setSelectedProjectManagerFilter([]);
    setSelectedCategoryFilter("");
    setSelectedPriorityFilter([]);
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const toggleActiveFilter = () => {
    setShowActiveOnly(!showActiveOnly);
  };

  // ========== Drag & Drop ==========
  const reorderColumns = (fromIndex, toIndex) => {
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setColumnOrder(newOrder);
  };

  const DraggableHeader = ({ column, index, children }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "COLUMN",
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    const [, drop] = useDrop({
      accept: "COLUMN",
      hover: (item) => {
        if (item.index !== index) {
          reorderColumns(item.index, index);
          item.index = index;
        }
      },
    });
    return (
      <th
        ref={(node) => drag(drop(node))}
        className={isDragging ? "vp2-dragging" : ""}
      >
        {children}
      </th>
    );
  };

  // Column Config
  const columnConfig = {
    index: {
      label: "#",
      render: (_, i) => <td key="index">{i + 1}</td>,
      sortable: false,
    },
    projectId: {
      label: "PROJECT ID",
      render: (p) => (
        <td key="projectId">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                display: "inline-block",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: p.active === 1 ? "#10b981" : "#ef4444",
              }}
            />
            <a
              href={`/project/${p.projectId}`}
              className="vp2-project-link"
              onClick={(e) => {
                if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                  navigate(`/project/${p.projectId}`);
                }
              }}
              style={{
                cursor: "pointer",
                textDecoration: "none",
                color: "#3b82f6",
                display: "inline-block",
              }}
            >
              {p.projectId}
            </a>
          </div>
        </td>
      ),
      sortable: false,
    },
    title: {
      label: "TITLE",
      render: (p) => (
        <td key="title">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <strong>{p.prTitle}</strong>
            {p.active !== 1 && (
              <span
                style={{
                  fontSize: "11px",
                  color: "#ef4444",
                  backgroundColor: "#fee2e2",
                  padding: "2px 6px",
                  borderRadius: "12px",
                }}
              >
                Inactive
              </span>
            )}
          </div>
        </td>
      ),
      sortable: false,
    },
    startDate: {
      label: "START DATE",
      render: (p) => (
        <td key="startDate">
          {p.prStartDate ? moment(p.prStartDate).format("L") : "—"}
        </td>
      ),
      sortable: true,
      sortKey: "prStartDate",
    },
    endDate: {
      label: "END DATE",
      render: (p) => (
        <td key="endDate">
          {p.prEndDate ? moment(p.prEndDate).format("L") : "—"}
        </td>
      ),
      sortable: true,
      sortKey: "prEndDate",
    },
    client: {
      label: "CLIENT",
      render: (p) => (
        <td key="client">
          <span
            className="vp2-client-cell"
            style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}
            title={
              customerMap[p.prClient]?.fullName ||
              formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
              "—"
            }
          >
            {formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
              "—"}
          </span>
        </td>
      ),
      sortable: false,
    },
    type: {
      label: "TYPE",
      render: (p) => (
        <td key="type">
          <Badge bg="light" className="vp2-type-badge">
            {getDisplayNameFromGuid(p.prTypes, "type")}
          </Badge>
        </td>
      ),
      sortable: false,
    },
    status: {
      label: "STATUS",
      render: (p) => {
        const statusName = getDisplayNameFromGuid(p.projectStatus, "status");
        const statusId = p.projectStatus;

        const getStatusClass = () => {
          const name = statusName.toLowerCase().replace(/\s+/g, "-");
          const id = statusId ? statusId.toLowerCase() : "";

          if (name === "idea") return "vp2-status-idea";
          if (name === "discussion") return "vp2-status-discussion";
          if (name === "proposal") return "vp2-status-proposal";
          if (name === "dev") return "vp2-status-dev";
          if (name === "sit") return "vp2-status-sit";
          if (name === "uat") return "vp2-status-uat";
          if (name === "go-live" || name === "go live")
            return "vp2-status-go-live";
          if (name === "hypercare") return "vp2-status-hypercare";
          if (name === "closed") return "vp2-status-closed";
          if (name === "cancelled") return "vp2-status-cancelled";
          if (name === "open") return "vp2-status-open";
          if (name === "new") return "vp2-status-new";
          if (name === "on hold" || name === "on-hold")
            return "vp2-status-on-hold";
          if (name === "in-progress") return "vp2-status-inprogress";
          return `vp2-status-${id}`;
        };

        return (
          <td key="status">
            <span className={`vp2-status-badge ${getStatusClass()}`}>
              {statusName}
            </span>
          </td>
        );
      },
      sortable: false,
    },
    subStatus: {
      label: "SUB STATUS",
      render: (p) => {
        const subStatusName = getDisplayNameFromGuid(
          p.projectSubStatus,
          "substatus",
        );

        const getSubStatusClass = () => {
          const name = subStatusName.toLowerCase().replace(/\s+/g, "-");

          const classMap = {
            draft: "vp2-substatus-draft",
            submitted: "vp2-substatus-submitted",
            approved: "vp2-substatus-approved",
            "under-evaluation": "vp2-substatus-under-evaluation",
            analysis: "vp2-substatus-analysis",
            "development-started": "vp2-substatus-development-started",
            "development-completed": "vp2-substatus-development-completed",
            development: "vp2-substatus-development",
            dev: "vp2-substatus-dev",
            fixing: "vp2-substatus-fixing",
            "ready-for-testing": "vp2-substatus-ready-for-testing",
            "testing-in-progress": "vp2-substatus-testing-in-progress",
            "testing-passed": "vp2-substatus-testing-passed",
            "testing-failed": "vp2-substatus-testing-failed",
            "uat-in-progress": "vp2-substatus-uat-in-progress",
            rejected: "vp2-substatus-rejected",
            "ready-for-deployment": "vp2-substatus-ready-for-deployment",
            deployed: "vp2-substatus-deployed",
            monitoring: "vp2-substatus-monitoring",
            completed: "vp2-substatus-completed",
            verified: "vp2-substatus-verified",
            delivered: "vp2-substatus-delivered",
            "budget-issue": "vp2-substatus-budget-issue",
            "scope-change": "vp2-substatus-scope-change",
            "client-decision": "vp2-substatus-client-decision",
            "client-dependency": "vp2-substatus-client-dependency",
            "resource-constraint": "vp2-substatus-resource-constraint",
            "technical-blocker": "vp2-substatus-technical-blocker",
            duplicate: "vp2-substatus-duplicate",
            "request-logged": "vp2-substatus-request-logged",
            "pending-review": "vp2-substatus-pending-review",
            captured: "vp2-substatus-captured",
            "issue-resolution": "vp2-substatus-issue-resolution",
            internal: "vp2-substatus-internal",
            client: "vp2-substatus-client",
          };

          return classMap[name] || "vp2-substatus-badge";
        };

        return (
          <td key="subStatus">
            <span className={`vp2-substatus-badge ${getSubStatusClass()}`}>
              {subStatusName}
            </span>
          </td>
        );
      },
      sortable: false,
    },
    category: {
      label: "CATEGORY",
      render: (p) => <td key="category">{p.projectCategory || "—"}</td>,
      sortable: false,
    },
    priority: {
      label: "PRIORITY",
      render: (p) => (
        <td key="priority">
          {p.projectPriority ? (
            <span
              style={{
                backgroundColor: getPriorityColor(p.projectPriority),
                color: "white",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                display: "inline-block",
              }}
            >
              {p.projectPriority}
            </span>
          ) : (
            "—"
          )}
        </td>
      ),
      sortable: true,
      sortKey: "projectPriority",
    },
  };

  // Active Filters Display
  const getFilterLabel = (filterType, value) => {
    switch (filterType) {
      case "status":
        return statusOptions.find((opt) => opt.value === value)?.label || value;
      case "substatus":
        return (
          subStatusOptions.find((opt) => opt.value === value)?.label || value
        );
      case "customer":
        return (
          customerOptions.find((opt) => opt.value === value)?.label || value
        );
      case "type":
        return typeOptions.find((opt) => opt.value === value)?.label || value;
      case "salesperson":
        return (
          resourceOptions.find((opt) => opt.value === value)?.label || value
        );
      case "manager":
        return (
          resourceOptions.find((opt) => opt.value === value)?.label || value
        );
      case "priority":
        return (
          PRIORITY_OPTIONS.find((opt) => opt.value === value)?.label || value
        );
      default:
        return value;
    }
  };

  const ActiveFilters = () => {
    const filters = [
      ...selectedStatusFilter.map((v) => ({
        label: `Status: ${getFilterLabel("status", v)}`,
        onRemove: () =>
          setSelectedStatusFilter((prev) => prev.filter((s) => s !== v)),
      })),
      ...selectedSubStatusFilter.map((v) => ({
        label: `Sub Status: ${getFilterLabel("substatus", v)}`,
        onRemove: () =>
          setSelectedSubStatusFilter((prev) => prev.filter((s) => s !== v)),
      })),
      ...selectedCustomerFilter.map((v) => ({
        label: `Customer: ${getFilterLabel("customer", v)}`,
        onRemove: () =>
          setSelectedCustomerFilter((prev) => prev.filter((c) => c !== v)),
      })),
      ...selectedTypeFilter.map((v) => ({
        label: `Type: ${getFilterLabel("type", v)}`,
        onRemove: () =>
          setSelectedTypeFilter((prev) => prev.filter((t) => t !== v)),
      })),
      ...selectedSalespersonFilter.map((v) => ({
        label: `Salesperson: ${getFilterLabel("salesperson", v)}`,
        onRemove: () =>
          setSelectedSalespersonFilter((prev) => prev.filter((s) => s !== v)),
      })),
      ...selectedProjectManagerFilter.map((v) => ({
        label: `Manager: ${getFilterLabel("manager", v)}`,
        onRemove: () =>
          setSelectedProjectManagerFilter((prev) =>
            prev.filter((m) => m !== v),
          ),
      })),
      ...selectedPriorityFilter.map((v) => ({
        label: `Priority: ${getFilterLabel("priority", v)}`,
        onRemove: () =>
          setSelectedPriorityFilter((prev) => prev.filter((p) => p !== v)),
      })),
    ];
    if (
      filters.length === 0 &&
      !globalSearch &&
      !selectedCategoryFilter &&
      !startDateFilter &&
      !endDateFilter
    )
      return null;
    return (
      <div className="vp2-active-filters-bar">
        <FaFilter className="vp2-filter-icon" />
        <div className="vp2-filter-chips">
          {globalSearch && (
            <Badge className="vp2-filter-chip">
              Search: "{globalSearch}"{" "}
              <FaTimes onClick={() => setGlobalSearch("")} />
            </Badge>
          )}
          {selectedCategoryFilter && (
            <Badge className="vp2-filter-chip">
              Category: {selectedCategoryFilter}{" "}
              <FaTimes onClick={() => setSelectedCategoryFilter("")} />
            </Badge>
          )}
          {startDateFilter && (
            <Badge className="vp2-filter-chip">
              Start ≥ {moment(startDateFilter).format("L")}{" "}
              <FaTimes onClick={() => setStartDateFilter("")} />
            </Badge>
          )}
          {endDateFilter && (
            <Badge className="vp2-filter-chip">
              End ≤ {moment(endDateFilter).format("L")}{" "}
              <FaTimes onClick={() => setEndDateFilter("")} />
            </Badge>
          )}
          {filters.map((f, i) => (
            <Badge key={i} className="vp2-filter-chip">
              {f.label} <FaTimes onClick={f.onRemove} />
            </Badge>
          ))}
        </div>
        <Button
          variant="link"
          className="vp2-clear-all-btn"
          onClick={resetAllFilters}
        >
          Clear All
        </Button>
      </div>
    );
  };

  // Persist preferences
  useEffect(() => {
    localStorage.setItem("vp2_columnOrder", JSON.stringify(columnOrder));
  }, [columnOrder]);

  if (!isMetaDataLoaded) {
    return (
      <div className="vp2-loading-container">
        <Spinner animation="border" variant="primary" />
        <span>Loading project data...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="vp2-view-projects-container">
        <Container fluid className="vp2-px-4 vp2-py-4">
          {/* Header */}
          <div className="vp2-page-header">
            <div>
              <p className="vp2-page-subtitle">Workspace / Active Projects</p>
              <h1 className="vp2-page-title">View Project</h1>
            </div>
            <div className="vp2-header-actions">
              <Button
                variant={showActiveOnly ? "success" : "secondary"}
                className="vp2-toggle-btn"
                onClick={toggleActiveFilter}
                style={{ borderRadius: "15px", marginRight: "10px" }}
              >
                {showActiveOnly ? <FaToggleOn /> : <FaToggleOff />}
                {showActiveOnly ? " Active Projects" : " Show Inactive"}
              </Button>
              <Button
                style={{ borderRadius: "15px" }}
                className="vp2-download-btn"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                <FaDownload /> {isDownloading ? "Downloading..." : "Download"}
              </Button>
            </div>
          </div>

          {/* Smart Filters Panel */}
          <div className="vp2-filters-panel">
            <div
              className="vp2-filters-header"
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="vp2-filters-title">
                <span className="vp2-filters-title-icon">
                  <FaEye className="vp2-title-icon" />
                </span>
                <span>Smart Filters</span>
              </div>
              {/* Global Search Row */}
              <div className="vp2-filter-row">
                <div className="vp2-filter-group vp2-search-group vp2-full-width">
                  <input
                    style={{ height: "3.5rem" }}
                    type="text"
                    className="vp2-search-input"
                    placeholder="Search by Project ID or Title..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                  />
                </div>
              </div>
              <span
                className={`vp2-toggle-icon ${showFilters ? "vp2-rotated" : ""}`}
              >
                ▼
              </span>
            </div>

            {showFilters && (
              <div className="vp2-filters-body">
                <div className="vp2-filter-grid">
                  {/* Status Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Status"
                      options={statusOptions}
                      selectedValues={selectedStatusFilter}
                      onChange={setSelectedStatusFilter}
                      placeholder="Select status..."
                    />
                  </div>

                  {/* Sub Status Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Sub Status"
                      options={subStatusOptions}
                      selectedValues={selectedSubStatusFilter}
                      onChange={setSelectedSubStatusFilter}
                      placeholder="Select sub status..."
                    />
                  </div>

                  {/* Customer Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Customer"
                      options={customerOptions}
                      selectedValues={selectedCustomerFilter}
                      onChange={setSelectedCustomerFilter}
                      placeholder="Select customers..."
                    />
                  </div>

                  {/* Project Type Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Project Type"
                      options={typeOptions}
                      selectedValues={selectedTypeFilter}
                      onChange={setSelectedTypeFilter}
                      placeholder="Select types..."
                    />
                  </div>

                  {/* Category Filter - Single Select Dropdown */}
                  <div className="vp2-filter-group">
                    <label>Category</label>
                    <select
                      style={{ height: "3.7rem" }}
                      value={selectedCategoryFilter}
                      onChange={(e) =>
                        setSelectedCategoryFilter(e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </select>
                  </div>

                  {/* Priority Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Priority"
                      options={PRIORITY_OPTIONS}
                      selectedValues={selectedPriorityFilter}
                      onChange={setSelectedPriorityFilter}
                      placeholder="Select priority..."
                    />
                  </div>

                  {/* Salesperson Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Salesperson"
                      options={resourceOptions}
                      selectedValues={selectedSalespersonFilter}
                      onChange={setSelectedSalespersonFilter}
                      placeholder="Select salespersons..."
                    />
                  </div>

                  {/* Project Manager Filter - Checkbox Multi-Select */}
                  <div className="vp2-filter-group">
                    <CheckboxMultiSelect
                      label="Project Manager"
                      options={resourceOptions}
                      selectedValues={selectedProjectManagerFilter}
                      onChange={setSelectedProjectManagerFilter}
                      placeholder="Select managers..."
                    />
                  </div>

                  {/* Start Date Filter */}
                  <div className="vp2-filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                    />
                  </div>

                  {/* End Date Filter */}
                  <div className="vp2-filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                    />
                  </div>
                </div>

                {/* Reset Button */}
                <div className="vp2-filters-actions">
                  <Button
                    variant="link"
                    className="vp2-reset-btn"
                    onClick={resetAllFilters}
                  >
                    <GrPowerReset /> Reset All Filters
                  </Button>
                </div>
              </div>
            )}
          </div>

          <ActiveFilters />

          {/* Table */}
          {projectsLoading ? (
            <div className="vp2-loading-container">
              <Spinner animation="border" variant="primary" />
              <span>Loading projects...</span>
            </div>
          ) : filteredProjects.length > 0 ? (
              <>
                {/* Add this project count section */}
    <div className="vp2-project-count-bar">
      <div className="vp2-project-count-info">
        <Badge bg="primary" className="vp2-count-badge">
          {filteredProjects.length}
        </Badge>
        <span className="vp2-count-text">
          {filteredProjects.length === 1 ? "Project" : "Projects"} Found
        </span>
        {showActiveOnly && filteredProjects.length !== projects.length && (
          <span className="vp2-filtered-hint">
            (Filtered from {projects.length} total {projects.length === 1 ? "project" : "projects"})
          </span>
        )}
      </div>
      {showActiveOnly && (
        <div className="vp2-active-indicator">
          <span className="vp2-active-dot" />
          Showing Active Projects Only
        </div>
      )}
    </div>
                <div className="vp2-table-wrapper">
              <div className="vp2-table-scroll">
                <table className="vp2-data-table">
                  <thead>
                    <tr>
                      {columnOrder.map((colKey, idx) => {
                        const col = columnConfig[colKey];
                        if (!col) return null;
                        return (
                          <DraggableHeader
                            key={colKey}
                            column={colKey}
                            index={idx}
                          >
                            {col.sortable ? (
                              <div
                                className="vp2-sortable-header"
                                onClick={() => handleSort(col.sortKey)}
                              >
                                {col.label} {renderSortArrow(col.sortKey)}
                              </div>
                            ) : (
                              col.label
                            )}
                          </DraggableHeader>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((project, idx) => (
                      <tr key={project.projectId || idx}>
                        {columnOrder.map((colKey) => {
                          const col = columnConfig[colKey];
                          return col ? col.render(project, idx) : null;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            
          ) : (
            <div className="vp2-no-data">
              {showActiveOnly
                ? "No active projects found."
                : "No projects found."}
            </div>
          )}
        </Container>

        {/* Edit Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Project</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Project ID</Form.Label>
                <Form.Select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  required
                >
                  <option value="">Select project</option>
                  {projects.map((p) => (
                    <option key={p.projectId} value={p.projectId}>
                      {p.projectId} - {p.prTitle}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      name="prTitle"
                      value={projectData.prTitle}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="prDescription"
                      value={projectData.prDescription}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="prStartDate"
                      value={projectData.prStartDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="prEndDate"
                      value={projectData.prEndDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Client</Form.Label>
                    <Form.Control
                      name="prClient"
                      value={projectData.prClient}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      name="prTypes"
                      value={projectData.prTypes}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {projectTypes.map((t) => (
                        <option key={t.projectTypeId} value={t.projectTypeId}>
                          {t.projectTypeDesc}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="projectStatus"
                      value={projectData.projectStatus}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {projectStatusList.map((s) => (
                        <option
                          key={s.projectStatusId}
                          value={s.projectStatusId}
                        >
                          {s.projectStatusDesc}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      name="projectCategory"
                      value={projectData.projectCategory}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      name="projectPriority"
                      value={projectData.projectPriority}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Very High">Very High</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Salesperson</Form.Label>
                    <Form.Control
                      name="salespersonCode"
                      value={projectData.salespersonCode}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Project Manager</Form.Label>
                    <Form.Control
                      name="currentProjectManager"
                      value={projectData.currentProjectManager}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </DndProvider>
  );
};

export default ViewProjects;

// import React, { useEffect, useState, useMemo, useCallback } from "react";
// import {
//   Container,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Row,
//   Col,
//   Badge,
//   Spinner,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import { useNavigate } from "react-router-dom";
// import Select from "react-select";
// import { DndProvider, useDrag, useDrop } from "react-dnd";
// import { HTML5Backend } from "react-dnd-html5-backend";
// import {
//   FaDownload,
//   FaTimes,
//   FaFilter,
//   FaEye,
//   FaToggleOn,
//   FaToggleOff,
// } from "react-icons/fa";
// import { GrPowerReset } from "react-icons/gr";
// import {
//   fetchProjectsByOrg,
//   fetchProjectsByTeamMember,
//   fetchProjectDetails,
//   updateProject,
// } from "../Api/ViewProjects";
// import {
//   fetchProjectBaseByOrg,
//   fetchProjectBaseByOrgAndMember,
// } from "../Api/ViewProjects";
// import { getTeamsByOrg } from "../Api/TeamMaster";
// import { getAllProjectStatus } from "../Api/ProjectStatusApi";
// import { fetchCustomerMasterByOrgId } from "../Api/ViewCustomerMaster";
// import { fetchProjectTypes, fetchResourceByOrgId } from "../Api/InvoiceApi";
// import { getProjectMetaNames } from "../Api/ProjectMaster";
// import "./ViewProject.css";

// // ============================================================
// // Utility Functions
// // ============================================================
// const formatWithId = (name, id) => {
//   if (!name && !id) return "";
//   if (name && id) return `${name}`;
//   return name || id;
// };

// // ============================================================
// // Data Mapping Function - Convert API snake_case to camelCase
// // ============================================================
// const mapApiProjectToComponent = (apiProject) => {
//   return {
//     projectId: apiProject.project_id,
//     prTitle: apiProject.pr_Title,
//     prDescription: apiProject.pr_Description,
//     prTags: apiProject.pr_Tags,
//     prStartDate: apiProject.pr_Start_Date,
//     prEndDate: apiProject.pr_End_Date,
//     prClient: apiProject.pr_Client,
//     prTypes: apiProject.pr_Types,
//     projectStatus: apiProject.project_Status,
//     projectSubStatus: apiProject.projectSubStatus,
//     projectCategory: apiProject.project_Category,
//     comments: apiProject.comments,
//     salespersonCode: apiProject.salesperson_Code,
//     currentProjectManager: apiProject.current_Project_Manager,
//     projectPriority: apiProject.projectPriority,
//     teamAssigned: apiProject.team_Assigned,
//     users_Id: apiProject.users_Id,
//     isFavourite: apiProject.isFavourite,
//     active: apiProject.active,
//     org_ID: apiProject.org_ID,
//     // Keep any other fields you might need
//     prozma_Pr_Id: apiProject.prozma_Pr_Id,
//     clientName: apiProject.clientName,
//     clientShortNames: apiProject.clientShortNames,
//     projectTypeDesc: apiProject.projectTypeDesc,
//     teamName: apiProject.teamName,
//     project_status_desc: apiProject.project_status_desc,
//     projectSubStatusDesc: apiProject.projectSubStatusDesc,
//     createdOn_date: apiProject.createdOn_date,
//     createdOn_time: apiProject.createdOn_time,
//     changedOn_date: apiProject.changedOn_date,
//     changedOn_time: apiProject.changedOn_time,
//     changedBy: apiProject.changedBy,
//     salesPersonName: apiProject.salesPersonName,
//     currentProjectManagerName: apiProject.currentProjectManagerName,
//     organisation_Name: apiProject.organisation_Name,
//     org_Short_name: apiProject.org_Short_name,
//     revenue_allowed: apiProject.revenue_allowed,
//     cost_allowed: apiProject.cost_allowed,
//     companycode: apiProject.companycode,
//     companyName: apiProject.companyName,
//     companyShortName: apiProject.companyShortName,
//     shortName: apiProject.shortName,
//   };
// };

// const customSelectStyles = {
//   control: (base, state) => ({
//     ...base,
//     minHeight: "40px",
//     borderRadius: "8px",
//     borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
//     boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.1)" : "none",
//     "&:hover": { borderColor: "#3b82f6" },
//     backgroundColor: "#ffffff",
//   }),
//   multiValue: (base) => ({
//     ...base,
//     backgroundColor: "#eff6ff",
//     borderRadius: "6px",
//   }),
//   multiValueLabel: (base) => ({
//     ...base,
//     color: "#1e40af",
//     fontSize: "12px",
//     fontWeight: 500,
//   }),
//   multiValueRemove: (base) => ({
//     ...base,
//     color: "#3b82f6",
//     "&:hover": {
//       backgroundColor: "#bfdbfe",
//       color: "#1e3a8a",
//       borderRadius: "4px",
//     },
//   }),
//   menuPortal: (base) => ({ ...base, zIndex: 9999 }),
// };

// // Priority options
// const PRIORITY_OPTIONS = [
//   { value: "Low", label: "Low", color: "#10b981" },
//   { value: "Medium", label: "Medium", color: "#f59e0b" },
//   { value: "High", label: "High", color: "#ef4444" },
//   { value: "Very High", label: "Very High", color: "#dc2626" },
// ];

// const getPriorityColor = (priority) => {
//   switch (priority) {
//     case "Very High":
//       return "#dc2626";
//     case "High":
//       return "#ef4444";
//     case "Medium":
//       return "#f59e0b";
//     case "Low":
//       return "#10b981";
//     default:
//       return "#6b7280";
//   }
// };

// // ============================================================
// // Main Component
// // ============================================================
// const ViewProjects = () => {
//   const navigate = useNavigate();
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = String(userDetails?.userId);
//   const orgId = String(userDetails?.organisationId);
//   const roles = userDetails?.roles || [];
//   const isAdmin = roles.includes("Admin");

//   // ========== State ==========
//   const [projects, setProjects] = useState([]);
//   const [projectsLoading, setProjectsLoading] = useState(true);
//   const [isDownloading, setIsDownloading] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [selectedProjectId, setSelectedProjectId] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

//   // Active/Inactive Toggle State - Default to Active (true)
//   const [showActiveOnly, setShowActiveOnly] = useState(true);

//   // Meta Data
//   const [metaDataMap, setMetaDataMap] = useState({
//     type: {},
//     status: {},
//     substatus: {},
//   });
//   const [isMetaDataLoaded, setIsMetaDataLoaded] = useState(false);

//   // Master Data
//   const [customers, setCustomers] = useState([]);
//   const [customerMap, setCustomerMap] = useState({});
//   const [projectTypes, setProjectTypes] = useState([]);
//   const [projectTypeMap, setProjectTypeMap] = useState({});
//   const [teams, setTeams] = useState([]);
//   const [teamMap, setTeamMap] = useState({});
//   const [resources, setResources] = useState([]);
//   const [activeResources, setActiveResources] = useState([]); // New state for active resources only
//   const [resourceMap, setResourceMap] = useState({});
//   const [projectStatusList, setProjectStatusList] = useState([]);

//   // Filter States - All filters displayed directly
//   const [globalSearch, setGlobalSearch] = useState("");
//   const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
//   const [selectedSubStatusFilter, setSelectedSubStatusFilter] = useState([]);
//   const [selectedCustomerFilter, setSelectedCustomerFilter] = useState([]);
//   const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
//   const [selectedSalespersonFilter, setSelectedSalespersonFilter] = useState(
//     [],
//   );
//   const [selectedProjectManagerFilter, setSelectedProjectManagerFilter] =
//     useState([]);
//   const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
//   const [selectedPriorityFilter, setSelectedPriorityFilter] = useState([]);
//   const [startDateFilter, setStartDateFilter] = useState("");
//   const [endDateFilter, setEndDateFilter] = useState("");

//   // UI State
//   const [showFilters, setShowFilters] = useState(true);

//   // Column Order
//   const defaultColumnOrder = [
//     "index",
//     "projectId",
//     "title",
//     "description",
//     "startDate",
//     "endDate",
//     "client",
//     "type",
//     "status",
//     "subStatus",
//     "category",
//     "priority",
//     "salesperson",
//     "projectManager",
//   ];
//   const [columnOrder, setColumnOrder] = useState(() => {
//     const saved = localStorage.getItem("vp2_columnOrder");
//     const savedOrder = saved ? JSON.parse(saved) : [];
//     return [
//       ...defaultColumnOrder.filter((col) => !savedOrder.includes(col)),
//       ...savedOrder,
//     ];
//   });

//   const [projectData, setProjectData] = useState({
//     prTitle: "",
//     prDescription: "",
//     prTags: "",
//     prStartDate: "",
//     prEndDate: "",
//     prClient: "",
//     prTypes: "",
//     teamAssigned: "",
//     projectStatus: "",
//     projectCategory: "",
//     comments: "",
//     salespersonCode: "",
//     currentProjectManager: "",
//     projectPriority: "",
//   });

//   // ========== Helper Functions ==========
//   const getDisplayNameFromGuid = useCallback(
//     (guid, entityType) => {
//       if (!guid) return "—";
//       const key = String(guid).toLowerCase();
//       return metaDataMap[entityType]?.[key] || "—";
//     },
//     [metaDataMap],
//   );

//   const handleSort = (key) => {
//     setSortConfig((prev) => ({
//       key,
//       direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
//     }));
//   };

//   const renderSortArrow = (key) => {
//     if (sortConfig.key !== key) return <span className="vp2-sort-icon">↕</span>;
//     return sortConfig.direction === "asc" ? (
//       <span className="vp2-sort-icon">↑</span>
//     ) : (
//       <span className="vp2-sort-icon">↓</span>
//     );
//   };

//   // ========== Data Fetching ==========
//   const fetchMetaNames = async (projectsList) => {
//     if (!projectsList?.length) return;
//     try {
//       const typeGuids = [
//         ...new Set(projectsList.map((p) => p.prTypes).filter(Boolean)),
//       ].join(",");
//       const statusGuids = [
//         ...new Set(projectsList.map((p) => p.projectStatus).filter(Boolean)),
//       ].join(",");
//       const subStatusGuids = [
//         ...new Set(projectsList.map((p) => p.projectSubStatus).filter(Boolean)),
//       ].join(",");
//       const response = await getProjectMetaNames(
//         typeGuids,
//         statusGuids,
//         subStatusGuids,
//       );
//       const typeMap = {},
//         statusMap = {},
//         substatusMap = {};
//       response.forEach((item) => {
//         const key = item.guid.toLowerCase();
//         if (item.entity === "TYPE") typeMap[key] = item.name;
//         if (item.entity === "STATUS") statusMap[key] = item.name;
//         if (item.entity === "SUBSTATUS") substatusMap[key] = item.name;
//       });
//       setMetaDataMap({
//         type: typeMap,
//         status: statusMap,
//         substatus: substatusMap,
//       });
//     } catch (err) {
//       console.error("Meta fetch error:", err);
//     } finally {
//       setIsMetaDataLoaded(true);
//     }
//   };

//   useEffect(() => {
//     const loadProjects = async () => {
//       try {
//         setProjectsLoading(true);
//         // Use the new API functions and map the data
//         const rawData = isAdmin
//           ? await fetchProjectBaseByOrg(orgId)
//           : await fetchProjectBaseByOrgAndMember(orgId, userId);
//         console.log("Raw API data:", rawData);

//         // Map the data to match component expectations
//         const mappedData = (rawData || []).map(mapApiProjectToComponent);
//         console.log("Mapped projects:", mappedData);

//         setProjects(mappedData);
//         await fetchMetaNames(mappedData);
//       } catch (error) {
//         toast.error("Failed to fetch projects");
//         console.error("Error fetching projects:", error);
//       } finally {
//         setProjectsLoading(false);
//       }
//     };
//     if (orgId && userId) loadProjects();
//   }, [orgId, userId, isAdmin]);

//   useEffect(() => {
//     const loadResources = async () => {
//       try {
//         const data = await fetchResourceByOrgId(orgId);
//         console.log("Fetched resources:", data);

//         // Filter to only active resources (active: true)
//         const onlyActiveResources = data.filter(
//           (resource) => resource.active === true,
//         );
//         console.log("Active resources only:", onlyActiveResources);

//         // Remove duplicates based on resourceId from active resources
//         const uniqueActiveResources = Array.from(
//           new Map(
//             onlyActiveResources.map((item) => [item.resourceId, item]),
//           ).values(),
//         );

//         // Store all resources (if needed elsewhere)
//         const uniqueAllResources = Array.from(
//           new Map(data.map((item) => [item.resourceId, item])).values(),
//         );
//         setResources(uniqueAllResources || []);

//         // Store only active resources for filters
//         setActiveResources(uniqueActiveResources || []);

//         // Create resource map for display (includes all resources to show names for existing projects)
//         setResourceMap(
//           Object.fromEntries(
//             uniqueAllResources.map((r) => [r.resourceId, r.name]),
//           ),
//         );
//       } catch (error) {
//         toast.error("Failed to load resources");
//       }
//     };
//     if (orgId) loadResources();
//   }, [orgId]);

//   useEffect(() => {
//     const loadProjectStatus = async () => {
//       try {
//         const data = await getAllProjectStatus();
//         const activeStatus = data.filter((s) => s.active);
//         setProjectStatusList(activeStatus);
//       } catch (error) {
//         toast.error("Failed to load statuses");
//       }
//     };
//     loadProjectStatus();
//   }, []);

//   // Update the loadCustomers useEffect:
//   useEffect(() => {
//     const loadCustomers = async () => {
//       try {
//         const data = await fetchCustomerMasterByOrgId(orgId);
//         console.log("Fetched customers:", data);
//         setCustomers(data || []);
//         // Store both short name and full name in the map
//         setCustomerMap(
//           Object.fromEntries(
//             data.map((c) => [
//               c.customerCode,
//               {
//                 shortName: c.shortNames,
//                 fullName: c.name || c.shortNames, // Use customerName if available, fallback to shortNames
//               },
//             ]),
//           ),
//         );
//       } catch (error) {
//         toast.error("Failed to load customers");
//       }
//     };
//     if (orgId) loadCustomers();
//   }, [orgId]);

//   useEffect(() => {
//     const loadProjectTypes = async () => {
//       try {
//         const data = await fetchProjectTypes(orgId);
//         const activeTypes = data.filter((t) => t.active);
//         setProjectTypes(activeTypes);
//         setProjectTypeMap(
//           Object.fromEntries(
//             activeTypes.map((t) => [t.projectTypeId, t.projectTypeDesc]),
//           ),
//         );
//       } catch (error) {
//         toast.error("Failed to load project types");
//       }
//     };
//     if (orgId) loadProjectTypes();
//   }, [orgId]);

//   useEffect(() => {
//     const loadTeams = async () => {
//       try {
//         const data = await getTeamsByOrg(orgId);
//         setTeams(data || []);
//         setTeamMap(Object.fromEntries(data.map((t) => [t.teamId, t.teamName])));
//       } catch (error) {
//         toast.error("Failed to load teams");
//       }
//     };
//     if (orgId) loadTeams();
//   }, [orgId]);

//   // ========== Derived Data ==========
//   const statusOptions = useMemo(() => {
//     const unique = new Map();
//     Object.entries(metaDataMap.status).forEach(([guid, name]) => {
//       if (!unique.has(name) && name !== guid) unique.set(name, guid);
//     });
//     return Array.from(unique.entries())
//       .map(([name, guid]) => ({ value: guid, label: name }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   }, [metaDataMap.status]);

//   const subStatusOptions = useMemo(() => {
//     const unique = new Map();
//     Object.entries(metaDataMap.substatus).forEach(([guid, name]) => {
//       if (!unique.has(name) && name !== guid) unique.set(name, guid);
//     });
//     return Array.from(unique.entries())
//       .map(([name, guid]) => ({ value: guid, label: name }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   }, [metaDataMap.substatus]);

//   const typeOptions = useMemo(() => {
//     const unique = new Map();
//     Object.entries(metaDataMap.type).forEach(([guid, name]) => {
//       if (!unique.has(name) && name !== guid) unique.set(name, guid);
//     });
//     return Array.from(unique.entries())
//       .map(([name, guid]) => ({ value: guid, label: name }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   }, [metaDataMap.type]);

//   const customerOptions = useMemo(() => {
//     // Remove duplicate customers based on customerCode
//     const uniqueCustomers = Array.from(
//       new Map(customers.map((c) => [c.customerCode, c])).values(),
//     );
//     return uniqueCustomers
//       .map((c) => ({ value: c.customerCode, label: c.shortNames }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   }, [customers]);

//   const resourceOptions = useMemo(() => {
//     // Create a unique map based on resourceId from ACTIVE resources only
//     const uniqueMap = new Map();
//     activeResources.forEach((resource) => {
//       if (!uniqueMap.has(resource.resourceId)) {
//         uniqueMap.set(resource.resourceId, {
//           value: resource.resourceId,
//           label: resource.name,
//         });
//       }
//     });
//     return Array.from(uniqueMap.values()).sort((a, b) =>
//       a.label.localeCompare(b.label),
//     );
//   }, [activeResources]);

//   // Filtered Projects - Combined search for Project ID and Title + all other filters + Active/Inactive toggle
//   const filteredProjects = useMemo(() => {
//     return projects
//       .filter((project) => {
//         // Active/Inactive filter
//         if (showActiveOnly && project.active !== 1) return false;

//         // Combined search: matches either Project ID or Title
//         const searchMatch = globalSearch
//           ? String(project.projectId)
//               .toLowerCase()
//               .includes(globalSearch.toLowerCase()) ||
//             project.prTitle?.toLowerCase().includes(globalSearch.toLowerCase())
//           : true;

//         if (!searchMatch) return false;
//         if (
//           selectedStatusFilter.length &&
//           !selectedStatusFilter.some((s) => s.value === project.projectStatus)
//         )
//           return false;
//         if (
//           selectedSubStatusFilter.length &&
//           !selectedSubStatusFilter.some(
//             (ss) => ss.value === project.projectSubStatus,
//           )
//         )
//           return false;
//         if (
//           selectedCustomerFilter.length &&
//           !selectedCustomerFilter.some((c) => c.value === project.prClient)
//         )
//           return false;
//         if (
//           selectedTypeFilter.length &&
//           !selectedTypeFilter.some((t) => t.value === project.prTypes)
//         )
//           return false;
//         if (
//           selectedSalespersonFilter.length &&
//           !selectedSalespersonFilter.some(
//             (s) => s.value === project.salespersonCode,
//           )
//         )
//           return false;
//         if (
//           selectedProjectManagerFilter.length &&
//           !selectedProjectManagerFilter.some(
//             (m) => m.value === project.currentProjectManager,
//           )
//         )
//           return false;
//         if (
//           selectedCategoryFilter &&
//           project.projectCategory !== selectedCategoryFilter
//         )
//           return false;
//         if (
//           selectedPriorityFilter.length &&
//           !selectedPriorityFilter.some(
//             (p) => p.value === project.projectPriority,
//           )
//         )
//           return false;
//         if (
//           startDateFilter &&
//           new Date(project.prStartDate) < new Date(startDateFilter)
//         )
//           return false;
//         if (
//           endDateFilter &&
//           new Date(project.prEndDate) > new Date(endDateFilter)
//         )
//           return false;
//         return true;
//       })
//       .sort((a, b) => {
//         if (!sortConfig.key) return 0;

//         // Handle priority sorting specially
//         if (sortConfig.key === "projectPriority") {
//           const priorityOrder = { Low: 1, Medium: 2, High: 3, "Very High": 4 };
//           const valA = priorityOrder[a.projectPriority] || 0;
//           const valB = priorityOrder[b.projectPriority] || 0;
//           return sortConfig.direction === "asc" ? valA - valB : valB - valA;
//         }

//         const valA = a[sortConfig.key]
//           ? new Date(a[sortConfig.key]).getTime()
//           : 0;
//         const valB = b[sortConfig.key]
//           ? new Date(b[sortConfig.key]).getTime()
//           : 0;
//         return sortConfig.direction === "asc" ? valA - valB : valB - valA;
//       });
//   }, [
//     projects,
//     globalSearch,
//     selectedStatusFilter,
//     selectedSubStatusFilter,
//     selectedCustomerFilter,
//     selectedTypeFilter,
//     selectedSalespersonFilter,
//     selectedProjectManagerFilter,
//     selectedCategoryFilter,
//     selectedPriorityFilter,
//     startDateFilter,
//     endDateFilter,
//     sortConfig,
//     showActiveOnly, // Add this dependency
//   ]);

//   // ========== Event Handlers ==========
//   const handleDownload = async () => {
//     setIsDownloading(true);
//     try {
//       const XLSX = await import("xlsx");
//       const worksheetData = [
//         [
//           "S.No",
//           "Project ID",
//           "Title",
//           "Description",
//           "Start Date",
//           "End Date",
//           "Client",
//           "Type",
//           "Status",
//           "Sub Status",
//           "Category",
//           "Priority",
//           "Salesperson",
//           "Project Manager",
//           "Status",
//         ],
//         ...filteredProjects.map((p, i) => [
//           i + 1,
//           p.projectId,
//           p.prTitle,
//           p.prDescription,
//           p.prStartDate ? moment(p.prStartDate).format("YYYY-MM-DD") : "",
//           p.prEndDate ? moment(p.prEndDate).format("YYYY-MM-DD") : "",
//           // Changed this line to use full name instead of short name
//           customerMap[p.prClient]?.fullName ||
//             customerMap[p.prClient]?.shortName ||
//             formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
//             "—",
//           getDisplayNameFromGuid(p.prTypes, "type"),
//           getDisplayNameFromGuid(p.projectStatus, "status"),
//           getDisplayNameFromGuid(p.projectSubStatus, "substatus"),
//           p.projectCategory || "",
//           p.projectPriority || "—",
//           formatWithId(resourceMap[p.salespersonCode], p.salespersonCode),
//           formatWithId(
//             resourceMap[p.currentProjectManager],
//             p.currentProjectManager,
//           ),
//           p.active === 1 ? "Active" : "Inactive",
//         ]),
//       ];
//       const ws = XLSX.utils.aoa_to_sheet(worksheetData);
//       const wb = XLSX.utils.book_new();
//       XLSX.utils.book_append_sheet(wb, ws, "Projects");
//       XLSX.writeFile(
//         wb,
//         `Projects_${orgId}_${moment().format("YYYYMMDD")}.xlsx`,
//       );
//       toast.success("Downloaded successfully!");
//     } catch (error) {
//       toast.error("Download failed");
//     } finally {
//       setIsDownloading(false);
//     }
//   };

//   const handleProjectSelect = async (projectId) => {
//     setSelectedProjectId(projectId);
//     try {
//       setIsLoading(true);
//       const data = await fetchProjectDetails(projectId);
//       // Map the data if it comes in snake_case
//       setProjectData({
//         prTitle: data.pr_Title || data.prTitle || "",
//         prDescription: data.pr_Description || data.prDescription || "",
//         prTags: data.pr_Tags || data.prTags || "",
//         prStartDate:
//           data.pr_Start_Date || data.prStartDate
//             ? moment(data.pr_Start_Date || data.prStartDate).format(
//                 "YYYY-MM-DD",
//               )
//             : "",
//         prEndDate:
//           data.pr_End_Date || data.prEndDate
//             ? moment(data.pr_End_Date || data.prEndDate).format("YYYY-MM-DD")
//             : "",
//         prClient: data.pr_Client || data.prClient || "",
//         prTypes: data.pr_Types || data.prTypes || "",
//         teamAssigned: data.team_Assigned || data.teamAssigned || "",
//         projectStatus: data.project_Status || data.projectStatus || "",
//         projectCategory: data.project_Category || data.projectCategory || "",
//         comments: data.comments || "",
//         salespersonCode: data.salesperson_Code || data.salespersonCode || "",
//         currentProjectManager:
//           data.current_Project_Manager || data.currentProjectManager || "",
//         projectPriority: data.projectPriority || "",
//       });
//     } catch (error) {
//       toast.error("Failed to fetch project details");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setProjectData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedProjectId) return toast.error("Select a project");
//     try {
//       setIsLoading(true);
//       await updateProject(selectedProjectId, projectData);
//       toast.success("Project updated!");
//       // Use the new API functions and map the data
//       const rawData = isAdmin
//         ? await fetchProjectBaseByOrg(orgId)
//         : await fetchProjectBaseByOrgAndMember(orgId, userId);
//       const mappedData = (rawData || []).map(mapApiProjectToComponent);
//       setProjects(mappedData);
//       setShowEditModal(false);
//     } catch (error) {
//       toast.error("Update failed");
//       console.error("Update error:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const resetAllFilters = () => {
//     setGlobalSearch("");
//     setSelectedStatusFilter([]);
//     setSelectedSubStatusFilter([]);
//     setSelectedCustomerFilter([]);
//     setSelectedTypeFilter([]);
//     setSelectedSalespersonFilter([]);
//     setSelectedProjectManagerFilter([]);
//     setSelectedCategoryFilter("");
//     setSelectedPriorityFilter([]);
//     setStartDateFilter("");
//     setEndDateFilter("");
//   };

//   // Toggle function for Active/Inactive
//   const toggleActiveFilter = () => {
//     setShowActiveOnly(!showActiveOnly);
//   };

//   // ========== Drag & Drop ==========
//   const reorderColumns = (fromIndex, toIndex) => {
//     const newOrder = [...columnOrder];
//     const [moved] = newOrder.splice(fromIndex, 1);
//     newOrder.splice(toIndex, 0, moved);
//     setColumnOrder(newOrder);
//   };

//   const DraggableHeader = ({ column, index, children }) => {
//     const [{ isDragging }, drag] = useDrag({
//       type: "COLUMN",
//       item: { index },
//       collect: (monitor) => ({ isDragging: monitor.isDragging() }),
//     });
//     const [, drop] = useDrop({
//       accept: "COLUMN",
//       hover: (item) => {
//         if (item.index !== index) {
//           reorderColumns(item.index, index);
//           item.index = index;
//         }
//       },
//     });
//     return (
//       <th
//         ref={(node) => drag(drop(node))}
//         className={isDragging ? "vp2-dragging" : ""}
//       >
//         {children}
//       </th>
//     );
//   };

//   // Column Config
//   const columnConfig = {
//     index: {
//       label: "#",
//       render: (_, i) => <td key="index">{i + 1}</td>,
//       sortable: false,
//     },
//     projectId: {
//       label: "PROJECT ID",
//       render: (p) => (
//         <td key="projectId">
//           <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
//             <span
//               style={{
//                 display: "inline-block",
//                 width: "8px",
//                 height: "8px",
//                 borderRadius: "50%",
//                 backgroundColor: p.active === 1 ? "#10b981" : "#ef4444",
//               }}
//             />
//             <a
//               href={`/project/${p.projectId}`}
//               className="vp2-project-link"
//               onClick={(e) => {
//                 if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
//                   e.preventDefault();
//                   navigate(`/project/${p.projectId}`);
//                 }
//               }}
//               style={{
//                 cursor: "pointer",
//                 textDecoration: "none",
//                 color: "#3b82f6",
//                 display: "inline-block",
//               }}
//             >
//               {p.projectId}
//             </a>
//           </div>
//         </td>
//       ),
//       sortable: false,
//     },

//     title: {
//       label: "TITLE",
//       render: (p) => (
//         <td key="title">
//           <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
//             <strong>{p.prTitle}</strong>
//             {p.active !== 1 && (
//               <span
//                 style={{
//                   fontSize: "11px",
//                   color: "#ef4444",
//                   backgroundColor: "#fee2e2",
//                   padding: "2px 6px",
//                   borderRadius: "12px",
//                 }}
//               >
//                 Inactive
//               </span>
//             )}
//           </div>
//         </td>
//       ),
//       sortable: false,
//     },
//     // description: {
//     //   label: "DESCRIPTION",
//     //   render: (p) => (
//     //     <td key="description">
//     //       {p.prDescription || "—"}
//     //     </td>
//     //   ),
//     //   sortable: false,
//     // },
//     startDate: {
//       label: "START DATE",
//       render: (p) => (
//         <td key="startDate">
//           {p.prStartDate ? moment(p.prStartDate).format("L") : "—"}
//         </td>
//       ),
//       sortable: true,
//       sortKey: "prStartDate",
//     },
//     endDate: {
//       label: "END DATE",
//       render: (p) => (
//         <td key="endDate">
//           {p.prEndDate ? moment(p.prEndDate).format("L") : "—"}
//         </td>
//       ),
//       sortable: true,
//       sortKey: "prEndDate",
//     },
//     client: {
//       label: "CLIENT",
//       render: (p) => (
//         <td key="client">
//           <span
//             className="vp2-client-cell"
//             style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}
//             title={
//               customerMap[p.prClient]?.fullName ||
//               formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
//               "—"
//             }
//           >
//             {formatWithId(customerMap[p.prClient]?.shortName, p.prClient) ||
//               "—"}
//           </span>
//         </td>
//       ),
//       sortable: false,
//     },
//     type: {
//       label: "TYPE",
//       render: (p) => (
//         <td key="type">
//           <Badge bg="light" className="vp2-type-badge">
//             {getDisplayNameFromGuid(p.prTypes, "type")}
//           </Badge>
//         </td>
//       ),
//       sortable: false,
//     },
//     status: {
//       label: "STATUS",
//       render: (p) => {
//         const statusName = getDisplayNameFromGuid(p.projectStatus, "status");
//         const statusId = p.projectStatus;

//         // Create a CSS class name based on status name or ID
//         const getStatusClass = () => {
//           const name = statusName.toLowerCase().replace(/\s+/g, "-");
//           const id = statusId ? statusId.toLowerCase() : "";

//           // Map status names to specific classes
//           if (name === "idea") return "vp2-status-idea";
//           if (name === "discussion") return "vp2-status-discussion";
//           if (name === "proposal") return "vp2-status-proposal";
//           if (name === "dev") return "vp2-status-dev";
//           if (name === "sit") return "vp2-status-sit";
//           if (name === "uat") return "vp2-status-uat";
//           if (name === "go-live" || name === "go live")
//             return "vp2-status-go-live";
//           if (name === "hypercare") return "vp2-status-hypercare";
//           if (name === "closed") return "vp2-status-closed";
//           if (name === "cancelled") return "vp2-status-cancelled";
//           if (name === "open") return "vp2-status-open";
//           if (name === "new") return "vp2-status-new";
//           if (name === "on hold" || name === "on-hold")
//             return "vp2-status-on-hold";
//           if (name === "in-progress") return "vp2-status-inprogress";
//           // Fallback to ID-based class
//           return `vp2-status-${id}`;
//         };

//         return (
//           <td key="status">
//             <span className={`vp2-status-badge ${getStatusClass()}`}>
//               {statusName}
//             </span>
//           </td>
//         );
//       },
//       sortable: false,
//     },
//     subStatus: {
//       label: "SUB STATUS",
//       render: (p) => {
//         const subStatusName = getDisplayNameFromGuid(
//           p.projectSubStatus,
//           "substatus",
//         );

//         // Create a CSS class name based on sub-status name
//         const getSubStatusClass = () => {
//           const name = subStatusName.toLowerCase().replace(/\s+/g, "-");

//           // Map sub-status names to specific classes
//           const classMap = {
//             draft: "vp2-substatus-draft",
//             submitted: "vp2-substatus-submitted",
//             approved: "vp2-substatus-approved",
//             "under-evaluation": "vp2-substatus-under-evaluation",
//             analysis: "vp2-substatus-analysis",
//             "development-started": "vp2-substatus-development-started",
//             "development-completed": "vp2-substatus-development-completed",
//             development: "vp2-substatus-development",
//             dev: "vp2-substatus-dev",
//             fixing: "vp2-substatus-fixing",
//             "ready-for-testing": "vp2-substatus-ready-for-testing",
//             "testing-in-progress": "vp2-substatus-testing-in-progress",
//             "testing-passed": "vp2-substatus-testing-passed",
//             "testing-failed": "vp2-substatus-testing-failed",
//             "uat-in-progress": "vp2-substatus-uat-in-progress",
//             rejected: "vp2-substatus-rejected",
//             "ready-for-deployment": "vp2-substatus-ready-for-deployment",
//             deployed: "vp2-substatus-deployed",
//             monitoring: "vp2-substatus-monitoring",
//             completed: "vp2-substatus-completed",
//             verified: "vp2-substatus-verified",
//             delivered: "vp2-substatus-delivered",
//             "budget-issue": "vp2-substatus-budget-issue",
//             "scope-change": "vp2-substatus-scope-change",
//             "client-decision": "vp2-substatus-client-decision",
//             "client-dependency": "vp2-substatus-client-dependency",
//             "resource-constraint": "vp2-substatus-resource-constraint",
//             "technical-blocker": "vp2-substatus-technical-blocker",
//             duplicate: "vp2-substatus-duplicate",
//             "request-logged": "vp2-substatus-request-logged",
//             "pending-review": "vp2-substatus-pending-review",
//             captured: "vp2-substatus-captured",
//             "issue-resolution": "vp2-substatus-issue-resolution",
//             internal: "vp2-substatus-internal",
//             client: "vp2-substatus-client",
//           };

//           return classMap[name] || "vp2-substatus-badge";
//         };

//         return (
//           <td key="subStatus">
//             <span className={`vp2-substatus-badge ${getSubStatusClass()}`}>
//               {subStatusName}
//             </span>
//           </td>
//         );
//       },
//       sortable: false,
//     },
//     category: {
//       label: "CATEGORY",
//       render: (p) => <td key="category">{p.projectCategory || "—"}</td>,
//       sortable: false,
//     },
//     priority: {
//       label: "PRIORITY",
//       render: (p) => (
//         <td key="priority">
//           {p.projectPriority ? (
//             <span
//               style={{
//                 backgroundColor: getPriorityColor(p.projectPriority),
//                 color: "white",
//                 padding: "4px 10px",
//                 borderRadius: "12px",
//                 fontSize: "12px",
//                 fontWeight: "600",
//                 display: "inline-block",
//               }}
//             >
//               {p.projectPriority}
//             </span>
//           ) : (
//             "—"
//           )}
//         </td>
//       ),
//       sortable: true,
//       sortKey: "projectPriority",
//     },
//     // salesperson: {
//     //   label: "SALESPERSON",
//     //   render: (p) => (
//     //     <td key="salesperson">
//     //       {formatWithId(resourceMap[p.salespersonCode], p.salespersonCode) || "—"}
//     //     </td>
//     //   ),
//     //   sortable: false,
//     // },
//     // projectManager: {
//     //   label: "PROJECT MANAGER",
//     //   render: (p) => (
//     //     <td key="projectManager">
//     //       {formatWithId(resourceMap[p.currentProjectManager], p.currentProjectManager) || "—"}
//     //     </td>
//     //   ),
//     //   sortable: false,
//     // },
//   };

//   // Active Filters Display
//   const ActiveFilters = () => {
//     const filters = [
//       ...selectedStatusFilter.map((f) => ({
//         label: `Status: ${f.label}`,
//         onRemove: () =>
//           setSelectedStatusFilter((prev) =>
//             prev.filter((s) => s.value !== f.value),
//           ),
//       })),
//       ...selectedSubStatusFilter.map((f) => ({
//         label: `Sub Status: ${f.label}`,
//         onRemove: () =>
//           setSelectedSubStatusFilter((prev) =>
//             prev.filter((s) => s.value !== f.value),
//           ),
//       })),
//       ...selectedCustomerFilter.map((f) => ({
//         label: `Customer: ${f.label}`,
//         onRemove: () =>
//           setSelectedCustomerFilter((prev) =>
//             prev.filter((c) => c.value !== f.value),
//           ),
//       })),
//       ...selectedTypeFilter.map((f) => ({
//         label: `Type: ${f.label}`,
//         onRemove: () =>
//           setSelectedTypeFilter((prev) =>
//             prev.filter((t) => t.value !== f.value),
//           ),
//       })),
//       ...selectedSalespersonFilter.map((f) => ({
//         label: `Salesperson: ${f.label}`,
//         onRemove: () =>
//           setSelectedSalespersonFilter((prev) =>
//             prev.filter((s) => s.value !== f.value),
//           ),
//       })),
//       ...selectedProjectManagerFilter.map((f) => ({
//         label: `Manager: ${f.label}`,
//         onRemove: () =>
//           setSelectedProjectManagerFilter((prev) =>
//             prev.filter((m) => m.value !== f.value),
//           ),
//       })),
//       ...selectedPriorityFilter.map((f) => ({
//         label: `Priority: ${f.label}`,
//         onRemove: () =>
//           setSelectedPriorityFilter((prev) =>
//             prev.filter((p) => p.value !== f.value),
//           ),
//       })),
//     ];
//     if (
//       filters.length === 0 &&
//       !globalSearch &&
//       !selectedCategoryFilter &&
//       !startDateFilter &&
//       !endDateFilter
//     )
//       return null;
//     return (
//       <div className="vp2-active-filters-bar">
//         <FaFilter className="vp2-filter-icon" />
//         <div className="vp2-filter-chips">
//           {globalSearch && (
//             <Badge className="vp2-filter-chip">
//               Search: "{globalSearch}"{" "}
//               <FaTimes onClick={() => setGlobalSearch("")} />
//             </Badge>
//           )}
//           {selectedCategoryFilter && (
//             <Badge className="vp2-filter-chip">
//               Category: {selectedCategoryFilter}{" "}
//               <FaTimes onClick={() => setSelectedCategoryFilter("")} />
//             </Badge>
//           )}
//           {startDateFilter && (
//             <Badge className="vp2-filter-chip">
//               Start ≥ {moment(startDateFilter).format("L")}{" "}
//               <FaTimes onClick={() => setStartDateFilter("")} />
//             </Badge>
//           )}
//           {endDateFilter && (
//             <Badge className="vp2-filter-chip">
//               End ≤ {moment(endDateFilter).format("L")}{" "}
//               <FaTimes onClick={() => setEndDateFilter("")} />
//             </Badge>
//           )}
//           {filters.map((f, i) => (
//             <Badge key={i} className="vp2-filter-chip">
//               {f.label} <FaTimes onClick={f.onRemove} />
//             </Badge>
//           ))}
//         </div>
//         <Button
//           variant="link"
//           className="vp2-clear-all-btn"
//           onClick={resetAllFilters}
//         >
//           Clear All
//         </Button>
//       </div>
//     );
//   };

//   // Persist preferences
//   useEffect(() => {
//     localStorage.setItem("vp2_columnOrder", JSON.stringify(columnOrder));
//   }, [columnOrder]);

//   if (!isMetaDataLoaded) {
//     return (
//       <div className="vp2-loading-container">
//         <Spinner animation="border" variant="primary" />
//         <span>Loading project data...</span>
//       </div>
//     );
//   }

//   return (
//     <DndProvider backend={HTML5Backend}>
//       <div className="vp2-view-projects-container">
//         <Container fluid className="vp2-px-4 vp2-py-4">
//           {/* Header */}
//           <div className="vp2-page-header">
//             <div>
//               <p className="vp2-page-subtitle">Workspace / Active Projects</p>
//               <h1 className="vp2-page-title">View Project</h1>
//             </div>
//             <div className="vp2-header-actions">
//               {/* Active/Inactive Toggle Button */}
//               <Button
//                 variant={showActiveOnly ? "success" : "secondary"}
//                 className="vp2-toggle-btn"
//                 onClick={toggleActiveFilter}
//                 style={{ borderRadius: "15px", marginRight: "10px" }}
//               >
//                 {showActiveOnly ? <FaToggleOn /> : <FaToggleOff />}
//                 {showActiveOnly ? " Active Projects" : " Show Inactive"}
//               </Button>
//               <Button
//                 style={{ borderRadius: "15px" }}
//                 className="vp2-download-btn"
//                 onClick={handleDownload}
//                 disabled={isDownloading}
//               >
//                 <FaDownload /> {isDownloading ? "Downloading..." : "Download"}
//               </Button>
//             </div>
//           </div>

//           {/* Smart Filters Panel */}
//           <div className="vp2-filters-panel">
//             <div
//               className="vp2-filters-header"
//               onClick={() => setShowFilters(!showFilters)}
//             >
//               <div className="vp2-filters-title">
//                 <span className="vp2-filters-title-icon">
//                   <FaEye className="vp2-title-icon" />
//                 </span>
//                 <span>Smart Filters</span>
//               </div>
//               {/* Global Search Row */}
//               <div className="vp2-filter-row">
//                 <div className="vp2-filter-group vp2-search-group vp2-full-width">
//                   <input
//                     style={{ height: "3.5rem" }}
//                     type="text"
//                     className="vp2-search-input"
//                     placeholder="Search by Project ID or Title..."
//                     value={globalSearch}
//                     onChange={(e) => setGlobalSearch(e.target.value)}
//                   />
//                 </div>
//               </div>
//               <span
//                 className={`vp2-toggle-icon ${showFilters ? "vp2-rotated" : ""}`}
//               >
//                 ▼
//               </span>
//             </div>

//             {showFilters && (
//               <div className="vp2-filters-body">
//                 <div className="vp2-filter-grid">
//                   {/* Status Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Status</label>
//                     <Select
//                       isMulti
//                       options={statusOptions}
//                       value={selectedStatusFilter}
//                       onChange={setSelectedStatusFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select status..."
//                     />
//                   </div>

//                   {/* Sub Status Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Sub Status</label>
//                     <Select
//                       isMulti
//                       options={subStatusOptions}
//                       value={selectedSubStatusFilter}
//                       onChange={setSelectedSubStatusFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select sub status..."
//                     />
//                   </div>

//                   {/* Customer Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Customer</label>
//                     <Select
//                       isMulti
//                       options={customerOptions}
//                       value={selectedCustomerFilter}
//                       onChange={setSelectedCustomerFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select customers..."
//                     />
//                   </div>

//                   {/* Project Type Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Project Type</label>
//                     <Select
//                       isMulti
//                       options={typeOptions}
//                       value={selectedTypeFilter}
//                       onChange={setSelectedTypeFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select types..."
//                     />
//                   </div>

//                   {/* Category Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Category</label>
//                     <select
//                       style={{ height: "3.7rem" }}
//                       value={selectedCategoryFilter}
//                       onChange={(e) =>
//                         setSelectedCategoryFilter(e.target.value)
//                       }
//                     >
//                       <option value="">All</option>
//                       <option value="Internal">Internal</option>
//                       <option value="External">External</option>
//                     </select>
//                   </div>

//                   {/* Priority Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Priority</label>
//                     <Select
//                       isMulti
//                       options={PRIORITY_OPTIONS}
//                       value={selectedPriorityFilter}
//                       onChange={setSelectedPriorityFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select priority..."
//                     />
//                   </div>

//                   {/* Salesperson Filter - Now only shows active resources */}
//                   <div className="vp2-filter-group">
//                     <label>Salesperson</label>
//                     <Select
//                       isMulti
//                       options={resourceOptions}
//                       value={selectedSalespersonFilter}
//                       onChange={setSelectedSalespersonFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select salespersons..."
//                     />
//                   </div>

//                   {/* Project Manager Filter - Now only shows active resources */}
//                   <div className="vp2-filter-group">
//                     <label>Project Manager</label>
//                     <Select
//                       isMulti
//                       options={resourceOptions}
//                       value={selectedProjectManagerFilter}
//                       onChange={setSelectedProjectManagerFilter}
//                       styles={customSelectStyles}
//                       menuPortalTarget={document.body}
//                       placeholder="Select managers..."
//                     />
//                   </div>

//                   {/* Start Date Filter */}
//                   <div className="vp2-filter-group">
//                     <label>Start Date</label>
//                     <input
//                       type="date"
//                       value={startDateFilter}
//                       onChange={(e) => setStartDateFilter(e.target.value)}
//                     />
//                   </div>

//                   {/* End Date Filter */}
//                   <div className="vp2-filter-group">
//                     <label>End Date</label>
//                     <input
//                       type="date"
//                       value={endDateFilter}
//                       onChange={(e) => setEndDateFilter(e.target.value)}
//                     />
//                   </div>
//                 </div>

//                 {/* Reset Button */}
//                 <div className="vp2-filters-actions">
//                   <Button
//                     variant="link"
//                     className="vp2-reset-btn"
//                     onClick={resetAllFilters}
//                   >
//                     <GrPowerReset /> Reset All Filters
//                   </Button>
//                 </div>
//               </div>
//             )}
//           </div>

//           <ActiveFilters />

//           {/* Table */}
//           {projectsLoading ? (
//             <div className="vp2-loading-container">
//               <Spinner animation="border" variant="primary" />
//               <span>Loading projects...</span>
//             </div>
//           ) : filteredProjects.length > 0 ? (
//             <div className="vp2-table-wrapper">
//               <div className="vp2-table-scroll">
//                 <table className="vp2-data-table">
//                   <thead>
//                     <tr>
//                       {columnOrder.map((colKey, idx) => {
//                         const col = columnConfig[colKey];
//                         if (!col) return null;
//                         return (
//                           <DraggableHeader
//                             key={colKey}
//                             column={colKey}
//                             index={idx}
//                           >
//                             {col.sortable ? (
//                               <div
//                                 className="vp2-sortable-header"
//                                 onClick={() => handleSort(col.sortKey)}
//                               >
//                                 {col.label} {renderSortArrow(col.sortKey)}
//                               </div>
//                             ) : (
//                               col.label
//                             )}
//                           </DraggableHeader>
//                         );
//                       })}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {filteredProjects.map((project, idx) => (
//                       <tr key={project.projectId || idx}>
//                         {columnOrder.map((colKey) => {
//                           const col = columnConfig[colKey];
//                           return col ? col.render(project, idx) : null;
//                         })}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           ) : (
//             <div className="vp2-no-data">
//               {showActiveOnly
//                 ? "No active projects found."
//                 : "No projects found."}
//             </div>
//           )}
//         </Container>

//         {/* Edit Modal */}
//         <Modal
//           show={showEditModal}
//           onHide={() => setShowEditModal(false)}
//           size="lg"
//           centered
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>Edit Project</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <Form onSubmit={handleSubmit}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Project ID</Form.Label>
//                 <Form.Select
//                   value={selectedProjectId}
//                   onChange={(e) => handleProjectSelect(e.target.value)}
//                   required
//                 >
//                   <option value="">Select project</option>
//                   {projects.map((p) => (
//                     <option key={p.projectId} value={p.projectId}>
//                       {p.projectId} - {p.prTitle}
//                     </option>
//                   ))}
//                 </Form.Select>
//               </Form.Group>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Title</Form.Label>
//                     <Form.Control
//                       name="prTitle"
//                       value={projectData.prTitle}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Description</Form.Label>
//                     <Form.Control
//                       as="textarea"
//                       rows={2}
//                       name="prDescription"
//                       value={projectData.prDescription}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Start Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="prStartDate"
//                       value={projectData.prStartDate}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>End Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       name="prEndDate"
//                       value={projectData.prEndDate}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Client</Form.Label>
//                     <Form.Control
//                       name="prClient"
//                       value={projectData.prClient}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Type</Form.Label>
//                     <Form.Select
//                       name="prTypes"
//                       value={projectData.prTypes}
//                       onChange={handleInputChange}
//                     >
//                       <option value="">Select</option>
//                       {projectTypes.map((t) => (
//                         <option key={t.projectTypeId} value={t.projectTypeId}>
//                           {t.projectTypeDesc}
//                         </option>
//                       ))}
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Status</Form.Label>
//                     <Form.Select
//                       name="projectStatus"
//                       value={projectData.projectStatus}
//                       onChange={handleInputChange}
//                     >
//                       <option value="">Select</option>
//                       {projectStatusList.map((s) => (
//                         <option
//                           key={s.projectStatusId}
//                           value={s.projectStatusId}
//                         >
//                           {s.projectStatusDesc}
//                         </option>
//                       ))}
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Category</Form.Label>
//                     <Form.Select
//                       name="projectCategory"
//                       value={projectData.projectCategory}
//                       onChange={handleInputChange}
//                     >
//                       <option value="">Select</option>
//                       <option value="Internal">Internal</option>
//                       <option value="External">External</option>
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Priority</Form.Label>
//                     <Form.Select
//                       name="projectPriority"
//                       value={projectData.projectPriority}
//                       onChange={handleInputChange}
//                     >
//                       <option value="">Select Priority</option>
//                       <option value="Low">Low</option>
//                       <option value="Medium">Medium</option>
//                       <option value="High">High</option>
//                       <option value="Very High">Very High</option>
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Salesperson</Form.Label>
//                     <Form.Control
//                       name="salespersonCode"
//                       value={projectData.salespersonCode}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <Row>
//                 <Col>
//                   <Form.Group>
//                     <Form.Label>Project Manager</Form.Label>
//                     <Form.Control
//                       name="currentProjectManager"
//                       value={projectData.currentProjectManager}
//                       onChange={handleInputChange}
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//               <div className="d-flex justify-content-end gap-2 mt-3">
//                 <Button
//                   variant="secondary"
//                   onClick={() => setShowEditModal(false)}
//                 >
//                   Cancel
//                 </Button>
//                 <Button variant="primary" type="submit" disabled={isLoading}>
//                   {isLoading ? "Updating..." : "Update"}
//                 </Button>
//               </div>
//             </Form>
//           </Modal.Body>
//         </Modal>
//       </div>
//     </DndProvider>
//   );
// };

// export default ViewProjects;
